import React, { useMemo, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { isGlobalAdmin } from '../../../Model/User/IUser.js'
import { StatsBarChart } from '../AdminStatsPage/StatsBarChart.js'
import { Card, KpiTile } from '../AdminStatsPage/StatsUi.js'
import { SpinChrome } from './SpinChrome.js'
import { InfoTip } from './SpinUi.js'
import { perSourceAverages, reachHistogram, sizeHistogram, sourcesOf, unityHistogram } from './spinMetrics.logic.js'
import { useSelectedRun } from './useSelectedRun.js'
import { useSpinMetrics } from './useSpinData.js'

/** Compact link tile to a sub-page, preserving the current run/taxonomy query string. */
const LinkCard: React.FC<{ title: string; to: string; children: React.ReactNode }> = ({ title, to, children }) => (
  <Card title={title} to={to}>
    <p className="text-sm text-gray-500">{children}</p>
  </Card>
)

/**
 * SPIN dashboard (hub). Whole-set KPIs + whole-set size distribution + an honestly-labelled cohesion
 * teaser (unity/reach/per-source over only the largest N SPINs), plus links out to the focused
 * sub-pages. See sibling pages: Spin{Cohesion,Compare,Growth,Drift,Flow}Page.
 */
export const AdminSpinPage = () => {
  const user = useGetAuthenticatedUser()
  const sel = useSelectedRun()
  const { selected } = sel
  const [params] = useSearchParams()
  const qs = params.toString()
  const suffix = qs ? `?${qs}` : ''

  const { metrics, loading: metricsLoading, error: metricsError } = useSpinMetrics(selected)
  const spins = metrics?.spins ?? []
  const sources = useMemo(() => sourcesOf(spins), [spins])
  const [unitySource, setUnitySource] = useState('Consensus')
  const effectiveSource = sources.includes(unitySource) ? unitySource : (sources[0] ?? '')

  const sizeData = useMemo(() => sizeHistogram(metrics?.sizeDistribution ?? []), [metrics])
  const unityData = useMemo(() => unityHistogram(spins, effectiveSource), [spins, effectiveSource])
  const reachData = useMemo(() => reachHistogram(spins, effectiveSource), [spins, effectiveSource])
  const sourceAverages = useMemo(() => perSourceAverages(spins, sources), [spins, sources])

  if (!user) return <></>
  if (!isGlobalAdmin(user)) return <Navigate to="/home" replace />

  const snap = metrics?.snapshot
  const spinCount = snap?.spinCount ?? 0
  const memberCount = snap?.memberCount ?? 0
  const avgMembers = spinCount > 0 ? memberCount / spinCount : 0
  const shown = spins.length

  return (
    <div className="space-y-8">
      <SpinChrome title="SPIN Metrics" sel={sel} />

      {metricsLoading && <p className="text-gray-500">Loading metrics…</p>}
      {metricsError && <p className="text-error">Could not load metrics: {metricsError}</p>}

      {!metricsLoading && !metricsError && snap && (
        <>
          {/* Whole-set snapshot (currency by assigned_at as of the selected run). */}
          <div className="grid grid-cols-3 gap-4">
            <KpiTile label="SPINs" value={spinCount.toLocaleString()} />
            <KpiTile label="Member OTUs" value={memberCount.toLocaleString()} />
            <KpiTile
              label={
                <>
                  Avg members / SPIN
                  <InfoTip tip="Member OTUs ÷ SPINs — average cluster size over the whole current set." />
                </>
              }
              value={avgMembers.toFixed(1)}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="Size distribution (members per SPIN) — full set">
              <StatsBarChart data={sizeData} />
            </Card>
            <Card title={`Per-source consensus — largest ${shown.toLocaleString()} SPINs`} to={`/admin/spin/cohesion${suffix}`}>
              {sourceAverages.length === 0 ? (
                <p className="text-gray-500 text-sm">No taxonomy resolved in this set.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="table table-xs">
                    <thead>
                      <tr>
                        <th>Source</th>
                        <th className="text-right">Avg unity</th>
                        <th className="text-right">Avg reach</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sourceAverages.map((r) => (
                        <tr key={r.source}>
                          <td>{r.source}</td>
                          <td className="text-right">{r.avgUnity == null ? '—' : r.avgUnity.toFixed(3)}</td>
                          <td className="text-right">{r.avgReach == null ? '—' : r.avgReach.toFixed(3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">
              Distribution source
              <InfoTip tip="Unity/reach distributions cover only the largest N SPINs (the detail set the API returns). The full-set view lives on the Cohesion page." />
            </span>
            <select
              className="select select-bordered select-xs"
              value={effectiveSource}
              onChange={(e) => setUnitySource(e.target.value)}
            >
              {sources.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title={`Unity distribution — largest ${shown.toLocaleString()} SPINs`} to={`/admin/spin/cohesion${suffix}`}>
              <StatsBarChart data={unityData} color="#8b5cf6" rotateLabels />
            </Card>
            <Card title={`Reach distribution — largest ${shown.toLocaleString()} SPINs`} to={`/admin/spin/cohesion${suffix}`}>
              <StatsBarChart data={reachData} color="#0ea5e9" rotateLabels />
            </Card>
          </div>
        </>
      )}

      {/* Deep-dive sub-pages. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <LinkCard title="Cohesion" to={`/admin/spin/cohesion${suffix}`}>
          Unity &amp; reach distributions, per-source consensus, most-mixed clusters.
        </LinkCard>
        <LinkCard title="Compare" to={`/admin/spin/compare${suffix}`}>
          Two algorithms side by side — structural cluster concordance.
        </LinkCard>
        {/* One tile, not two: the drill-down route needs a concrete reference id, so this page is also the
            only entry point to the triage list. Static link on purpose — a cohort count would have to come
            from the whole-set reference query, which is deliberately expensive and opt-in. */}
        <LinkCard title="Reference" to={`/admin/spin/reference${suffix}`}>
          Does SPIN split the same NCBI id / taxon / BOLD bin across clusters? Rank the ids that split{' '}
          <i>despite</i> a near-perfect match, then drill into one.
        </LinkCard>
        {/* Static link, like Reference and for the same reason: the cohort count comes from the whole-set
            singleton query (~3.3s), which is opt-in on the page itself. */}
        <LinkCard title="High-identity singletons" to={`/admin/spin/singletons${suffix}`}>
          Clusters of one whose single member still matches an NCBI reference near-perfectly — the plausible missed merges.
        </LinkCard>
        {/* Static link, like Reference and Singletons: any count worth showing here would come from the
            whole-set aggregation the page itself loads on demand (~8s). */}
        <LinkCard title="Top SPINs" to={`/admin/spin/leaderboard${suffix}`}>
          The biggest clusters by member OTUs, distinct sequences, orders and reads — which SPINs carry the most weight.
        </LinkCard>
        <LinkCard title="Growth" to={`/admin/spin/growth${suffix}`}>
          SPIN count &amp; recompute outcomes per run.
        </LinkCard>
        <LinkCard title="Drift" to={`/admin/spin/drift${suffix}`}>
          Taxonomy that moved while a cluster carried forward 1:1.
        </LinkCard>
        <LinkCard title="Membership flow" to={`/admin/spin/flow${suffix}`}>
          OTUs that stayed, left or joined across a recompute.
        </LinkCard>
        <LinkCard title="Raw export" to={`/admin/spin/export${suffix}`}>
          The SPIN set as CSV &amp; FASTA — representative sequences to BLAST, member sequences to re-cluster.
        </LinkCard>
        <LinkCard title="Definitions" to={`/admin/spin/about${suffix}`}>
          What unity &amp; reach mean, aligned to the implementation.
        </LinkCard>
      </div>
    </div>
  )
}
